// src/jornada/jornadaPage.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { IniciarJornada } from "../jornada/iniciarJornada";
import { FinalizarJornada } from "../jornada/finalizarJornada";
import useAuth from "../hooks/useAuth";
import { jornadaStore } from "../data/jornadaStore";

// --- Toast fijo (sin dependencias externas) ---
const Toast = ({ type = "info", children }) => {
  const [top, setTop] = useState(90);
  useEffect(() => {
    const calcTop = () => {
      const header = document.querySelector(".header");
      const navi = document.querySelector(".navi");
      const h = (header?.offsetHeight || 0) + (navi?.offsetHeight || 0);
      setTop(h > 0 ? h + 12 : 90);
    };
    calcTop();
    window.addEventListener("resize", calcTop);
    return () => window.removeEventListener("resize", calcTop);
  }, []);
  const base = {
    position: "fixed",
    top,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9999,
    padding: "12px 18px",
    borderRadius: 8,
    fontWeight: 600,
    boxShadow: "0 8px 24px rgba(0,0,0,.12)",
    pointerEvents: "none",
    maxWidth: "90vw",
    textAlign: "center",
  };
  const variants = {
    ok:    { background: "#e7f8ed", color: "#0f5132", border: "1px solid #b6f0c6" },
    error: { background: "#fdeceb", color: "#842029", border: "1px solid #f5c2c7" },
    info:  { background: "#e8f6ff", color: "#084298", border: "1px solid #b6d9ff" },
  };
  return <div style={{ ...base, ...(variants[type] || {}) }}>{children}</div>;
};

export const JornadaPage = () => {
  const { auth } = useAuth();

  const [jornadasEnProceso, setJornadasEnProceso] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [toast, setToast] = useState(null);
  const [forcedView, setForcedView] = useState(null);  // 'start' | 'finish' | null
  const [authExpired, setAuthExpired] = useState(false);
  const [hasPendingLocal, setHasPendingLocal] = useState(false);

  const toastDurationMs = 4000;
  const isFetchingRef = useRef(false);
  const unsubRef = useRef(null);

  // Bootstrap de la store y suscripción a cambios
  useEffect(() => {
    jornadaStore.bootstrap().catch(() => {});
    unsubRef.current = jornadaStore.subscribe(() => {
      if (!forcedView) fetchJornadasEnProceso({ refresh: true });
    });
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [forcedView]);

  const fetchJornadasEnProceso = async ({ refresh = false } = {}) => {
    try {
      if (forcedView) return;
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (!refresh) setInitialLoading(true);

      // La store decide online/offline.
      const active = await jornadaStore.getActive(); // objeto o null
      const local  = jornadaStore.getLocal();        // objeto local o null

      const pending = Boolean(local?.pendingFinish || local?.pendingCancel);

      if (!active) {
        // ✅ Preferimos mostrar la local si existe y NO está marcada como pending
        if (local && !(local.pendingFinish || local.pendingCancel)) {
          setJornadasEnProceso([local]);
        } else {
          setJornadasEnProceso([]);
        }
        setHasPendingLocal(pending);
        setAuthExpired(false);
      } else {
        // Oculta tareas marcadas como pending (no deben mostrarse como finalizables)
        const visible = (active.pendingFinish || active.pendingCancel) ? [] : [active];
        setJornadasEnProceso(visible);
        setHasPendingLocal(pending);
        setAuthExpired(false);
      }
    } catch (e) {
      if (e?.code === "AUTH_EXPIRED") {
        setAuthExpired(true);
        setJornadasEnProceso([]);
      }
    } finally {
      isFetchingRef.current = false;
      if (!refresh) setInitialLoading(false);
    }
  };

  // Carga inicial al tener usuario
  useEffect(() => {
    if (auth?.id) fetchJornadasEnProceso({ refresh: false });
  }, [auth?.id]);

  const hayActiva = jornadasEnProceso.length > 0;

  const jornadaActual = useMemo(() => {
    if (!hayActiva) return null;
    return [...jornadasEnProceso].sort(
      (a, b) =>
        new Date(b.inicio ?? b.created_at ?? b.fecha_inicio ?? 0) -
        new Date(a.inicio ?? a.created_at ?? a.fecha_inicio ?? 0)
    )[0];
  }, [hayActiva, jornadasEnProceso]);

  const toastThenRefresh = ({ type, text, keepView, ms = toastDurationMs }) => {
    setToast({ type, text });
    setForcedView(keepView);
    window.setTimeout(async () => {
      setToast(null);
      setForcedView(null);
      await fetchJornadasEnProceso({ refresh: true });
    }, ms);
  };

  if (initialLoading) return <p>Cargando datos...</p>;

  const uiLocked = Boolean(forcedView);

  // 🔑 Cambio importante: mientras haya jornada local pendiente, NO mostramos Iniciar/Finalizar
  const showIniciar   = !authExpired && !hasPendingLocal && (forcedView ? forcedView === "start"  : !hayActiva);
  const showFinalizar = !authExpired && !hasPendingLocal && (forcedView ? forcedView === "finish" :  hayActiva);

  const safeRefresh = () => { if (!forcedView) fetchJornadasEnProceso({ refresh: true }); };

  return (
    <div className="ticaje-frame">
      {toast && <Toast type={toast.type}>{toast.text}</Toast>}

      {hasPendingLocal && (
        <div
          style={{
            background: "#e8f6ff",
            border: "1px solid #b6d9ff",
            color: "#084298",
            padding: "10px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <b>Jornada pendiente de envío.</b> Has cerrado o cancelado una jornada sin conexión.
          Se enviará automáticamente al recuperar la red y <b>no podrás iniciar otra</b> hasta que se sincronice.
        </div>
      )}

      {authExpired && (
        <div
          style={{
            background: "#fff3cd",
            border: "1px solid #ffe69c",
            color: "#664d03",
            padding: "10px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <b>Token inválido o expirado.</b> Por seguridad debes iniciar sesión de nuevo.
        </div>
      )}

      {showIniciar && (
        <IniciarJornada
          uiLocked={uiLocked}
          onJornadaActualizada={safeRefresh}
          onSuccess={(msg) =>
            toastThenRefresh({
              type: "ok",
              text: msg || "Jornada iniciada correctamente.",
              keepView: "finish",   // tras iniciar saltamos a “Finalizar”
            })
          }
          onError={(msg) =>
            toastThenRefresh({
              type: "error",
              text: msg || "No se pudo iniciar la jornada.",
              keepView: "start",
            })
          }
        />
      )}

      {showFinalizar && (
        <FinalizarJornada
          uiLocked={uiLocked}
          jornada={jornadaActual}
          onJornadaActualizada={safeRefresh}
          onSuccess={(msg) =>
            toastThenRefresh({
              type: "ok",
              text: msg || "Jornada finalizada correctamente.",
              keepView: "start",   // tras cerrar/cancelar, volvemos a “Iniciar”
            })
          }
          onError={(msg) =>
            toastThenRefresh({
              type: "error",
              text: msg || "No se pudo finalizar la jornada.",
              keepView: "finish",
            })
          }
        />
      )}
    </div>
  );
};

export default JornadaPage;
